import { useEffect, useId, useMemo, useState } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useController, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import type {
  DrawerVariantFacetOption,
  DrawerVariantFacets,
  DrawerVariantFacetsQuery,
  DrawerSystemVariantListItem,
} from '@kreart/shared';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { drawerSystemsApi } from '@/features/catalog/components/admin/api';
import { useDebouncedValue } from '@/lib/useDebouncedValue';
import { cn } from '@/lib/cn';

/**
 * Faceted drawer-system picker.
 *
 * Every facet is optional and multi-valued, and the options come from the
 * catalog rather than a hand-maintained list — so a combination that has no
 * products simply cannot be selected, and there is no "no exact match" state to
 * fall back from. Depth is a ranking hint only: an off-catalog 480mm drawer
 * surfaces the 500mm rows at the top instead of returning nothing.
 */

export const DRAWER_FACET_FIELDS = [
  'modelSertar',
  'tipSertar',
  'descriere',
  'hBox',
  'detaliu',
  'color',
  'depthMm',
] as const;
export type DrawerFacetField = (typeof DRAWER_FACET_FIELDS)[number];
export type DrawerFacetSelection = Partial<Record<DrawerFacetField, string[]>>;

const FACET_LABELS: Record<DrawerFacetField, string> = {
  modelSertar: 'Model sertar',
  tipSertar: 'Tip sertar',
  descriere: 'Descriere',
  hBox: 'H-box',
  detaliu: 'Detaliu',
  color: 'Culoare',
  depthMm: 'Adancime',
};

interface Props {
  name: string;
  label: string;
  /** The depth the drawer needs. Ranks results; never filters them. */
  nearDepthMm?: number;
  /** Label for an already-chosen variant that is not on the current page. */
  selectedLabel?: string;
  required?: boolean;
  onVariantSelected?: (variant: DrawerSystemVariantListItem) => void;
}

export function DrawerSystemSelector({
  name,
  label,
  nearDepthMm,
  selectedLabel,
  required,
  onVariantSelected,
}: Props) {
  const { t } = useTranslation();
  const groupId = useId();
  const [selection, setSelection] = useState<DrawerFacetSelection>({});
  const [search, setSearch] = useState('');
  const {
    field,
    fieldState: { error },
  } = useController({ name });
  const { clearErrors, trigger } = useFormContext();

  const debouncedSearch = useDebouncedValue(search, 250);
  const filters: DrawerVariantFacetsQuery = useMemo(
    () => ({
      active: true,
      includeDeleted: false,
      search: debouncedSearch.trim() || undefined,
      ...selection,
      depthMm: selection.depthMm?.map(Number),
      nearDepthMm,
    }),
    [nearDepthMm, debouncedSearch, selection],
  );

  // Facet counts ignore the free-text search so the chips stay stable while typing.
  const facetFilters = useMemo(() => ({ ...filters, search: undefined }), [filters]);
  const facetsQuery = useQuery({
    queryKey: ['catalog-drawer-system-variants', 'facets', facetFilters],
    queryFn: () => drawerSystemsApi.variantFacets(facetFilters),
    meta: { silent: true },
    staleTime: 60_000,
    // Narrowing the selection changes the query key. Without this the chips and
    // the result rows unmount while the next response is in flight, which both
    // flickers and — because the row under the pointer is replaced between
    // mousedown and mouseup — swallows the click that was picking it.
    placeholderData: keepPreviousData,
  });
  const variantsQuery = useQuery({
    queryKey: ['catalog-drawer-system-variants', 'search', filters],
    queryFn: () =>
      drawerSystemsApi.searchVariants({ ...filters, page: 1, pageSize: 40 }).then((res) => res),
    meta: { silent: true },
    staleTime: 60_000,
    placeholderData: keepPreviousData,
  });

  const facets = facetsQuery.data;
  const options = variantsQuery.data?.items ?? [];
  const total = variantsQuery.data?.total ?? 0;
  const selected = options.find((opt) => opt.id === field.value) ?? null;
  const errorId = `${groupId}-error`;

  // Drop selected facet values that the catalog no longer offers — otherwise a
  // stale chip could pin the list to zero rows with no visible cause.
  const facetsSettled = !facetsQuery.isFetching && !facetsQuery.isPlaceholderData;
  useEffect(() => {
    if (!facets || !facetsSettled) return;
    setSelection((current) => {
      let changed = false;
      const next: DrawerFacetSelection = {};
      for (const facetField of DRAWER_FACET_FIELDS) {
        const picked = current[facetField];
        if (!picked?.length) continue;
        const available = new Set(facets[facetField].map((opt) => opt.value.toLowerCase()));
        const kept = picked.filter((value) => available.has(value.toLowerCase()));
        if (kept.length !== picked.length) changed = true;
        if (kept.length) next[facetField] = kept;
      }
      return changed ? next : current;
    });
  }, [facets, facetsSettled]);

  const toggle = (facetField: DrawerFacetField, value: string) => {
    setSelection((current) => {
      const picked = current[facetField] ?? [];
      const isOn = picked.some((entry) => entry.toLowerCase() === value.toLowerCase());
      const kept = isOn
        ? picked.filter((entry) => entry.toLowerCase() !== value.toLowerCase())
        : [...picked, value];
      const next = { ...current };
      if (kept.length) next[facetField] = kept;
      else delete next[facetField];
      return next;
    });
  };

  const handleSelect = (option: DrawerSystemVariantListItem) => {
    field.onChange(option.id);
    clearErrors(name);
    onVariantSelected?.(option);
    window.setTimeout(() => void trigger(name), 0);
  };

  const activeFacets = DRAWER_FACET_FIELDS.filter(
    (facetField) => (facets?.[facetField]?.length ?? 0) > 0,
  );
  const hasSelection = Object.values(selection).some((values) => values && values.length > 0);

  return (
    <div>
      <div className="flex items-baseline justify-between">
        <Label htmlFor={`${groupId}-search`}>
          {label}
          {required ? ' *' : ''}
        </Label>
        {hasSelection && (
          <button
            type="button"
            className="text-xs text-muted-foreground underline hover:text-foreground"
            onClick={() => setSelection({})}
          >
            Reseteaza filtrele
          </button>
        )}
      </div>

      <div className="mt-1 space-y-2 rounded-md border border-border p-3">
        {field.value ? (
          <p className="rounded-md bg-accent px-3 py-2 text-sm text-accent-foreground">
            <span className="font-medium">Selectat:</span>{' '}
            {selected ? optionLabel(selected) : (selectedLabel ?? field.value)}
          </p>
        ) : null}

        {facetsQuery.isPending ? (
          <p className="text-sm text-muted-foreground">{t('pricing.searching')}</p>
        ) : (
          activeFacets.map((facetField) => (
            <FacetRow
              key={facetField}
              label={FACET_LABELS[facetField]}
              options={facets![facetField]}
              picked={selection[facetField] ?? []}
              onToggle={(value) => toggle(facetField, value)}
            />
          ))
        )}

        <Input
          id={`${groupId}-search`}
          value={search}
          placeholder={t('pricing.pickDrawerVariant') ?? 'Search drawer system'}
          aria-invalid={error ? true : undefined}
          aria-describedby={error ? errorId : undefined}
          onChange={(event) => setSearch(event.currentTarget.value)}
        />

        <div className="flex items-center justify-between text-xs text-muted-foreground">
          <span>
            {variantsQuery.isFetching
              ? t('pricing.searching')
              : `${total} ${total === 1 ? 'sistem' : 'sisteme'}`}
          </span>
          {nearDepthMm ? <span>Ordonat dupa apropierea de {nearDepthMm}mm</span> : null}
        </div>

        <div
          role="listbox"
          aria-label={label}
          className="max-h-64 overflow-y-auto rounded-md border border-border"
        >
          {options.length === 0 && !variantsQuery.isFetching ? (
            <p className="px-3 py-2 text-sm text-muted-foreground">
              {t('common.emptyState.title')}
            </p>
          ) : (
            options.map((opt) => (
              <button
                key={opt.id}
                type="button"
                role="option"
                aria-selected={field.value === opt.id}
                className={cn(
                  'block w-full px-3 py-2 text-left text-sm hover:bg-accent hover:text-accent-foreground',
                  field.value === opt.id && 'bg-accent text-accent-foreground',
                )}
                onMouseDown={(event) => {
                  event.preventDefault();
                  handleSelect(opt);
                }}
              >
                <span className="block truncate font-medium">{optionLabel(opt)}</span>
                <span className="block truncate text-xs text-muted-foreground">
                  {optionDescription(opt)}
                </span>
              </button>
            ))
          )}
        </div>
      </div>

      <input
        type="hidden"
        name={field.name}
        value={(field.value ?? '') as string}
        ref={field.ref}
        readOnly
      />
      {error && (
        <p id={errorId} className="mt-1 text-xs text-destructive" role="alert">
          {error.message}
        </p>
      )}
    </div>
  );
}

function FacetRow({
  label,
  options,
  picked,
  onToggle,
}: {
  label: string;
  options: DrawerVariantFacetOption[];
  picked: string[];
  onToggle: (value: string) => void;
}) {
  const isPicked = (value: string) =>
    picked.some((entry) => entry.toLowerCase() === value.toLowerCase());
  return (
    <div className="flex flex-wrap items-center gap-1.5">
      <span className="w-24 shrink-0 text-xs font-medium text-muted-foreground">{label}</span>
      {options.map((opt) => (
        <button
          key={opt.value}
          type="button"
          aria-pressed={isPicked(opt.value)}
          className={cn(
            'rounded-full border px-2.5 py-1 text-xs transition-colors',
            isPicked(opt.value)
              ? 'border-primary bg-primary text-primary-foreground'
              : 'border-border hover:bg-accent hover:text-accent-foreground',
          )}
          onClick={() => onToggle(opt.value)}
        >
          {opt.label}
          <span className="ml-1 opacity-60">{opt.count}</span>
        </button>
      ))}
    </div>
  );
}

function optionLabel(item: DrawerSystemVariantListItem): string {
  if (!item.systemName) return item.description;
  // Extract qualifiers from the system name that aren't in the description
  // (e.g. "Blumotion", "Tip-ON") by dropping segments whose words all appear
  // in the description already.
  const descWords = new Set(
    item.description.toLowerCase().replace(/[,]/g, ' ').split(/\s+/).filter(Boolean),
  );
  const suffix = item.systemName
    .split(/,\s*/)
    .flatMap((segment) => segment.trim().split(/\s+/))
    .filter((word) => word && !descWords.has(word.toLowerCase()))
    .join(' ');
  return suffix ? `${item.description} - ${suffix}` : item.description;
}

function optionDescription(item: DrawerSystemVariantListItem): string {
  const attrs = item.attributes;
  const details = [
    attrs?.tipSertar,
    attrs?.descriere,
    attrs?.detaliu,
    attrs?.hBox ? `H-box ${attrs.hBox}` : null,
    attrs?.depthMm ? `${attrs.depthMm}mm` : null,
    attrs?.color,
    attrs?.interior ? 'interior' : null,
    item.priceNet ? `${item.priceNet} ${item.currency}` : item.currency,
  ].filter(Boolean);
  return details.join(' - ');
}

export type { DrawerVariantFacets };
